import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { api } from '../api/client.js';
import type { UserRole } from '../../shared/types.js';

interface AuthUser {
  id: string;
  email: string;
  name: string;
  role: UserRole;
  dev_mode?: boolean;
}

interface AuthState {
  user: AuthUser | null;
  loading: boolean;
  error: string | null;
  initialized: boolean;
}

const initialState: AuthState = {
  user: null,
  loading: false,
  error: null,
  initialized: false,
};

export const fetchCurrentUser = createAsyncThunk('auth/fetchCurrentUser', async (_, { rejectWithValue }) => {
  const res = await api.get<{ user: AuthUser }>('/auth/me');
  if (!res.success) return rejectWithValue(res.error.message);
  return res.data.user;
});

export const loginWithMagicLink = createAsyncThunk('auth/loginMagicLink', async (email: string, { rejectWithValue }) => {
  const res = await api.post<{ message: string }>('/auth/login', { email });
  if (!res.success) return rejectWithValue(res.error.message);
  return res.data.message;
});

export const loginWithPassword = createAsyncThunk(
  'auth/loginPassword',
  async ({ email, password }: { email: string; password: string }, { rejectWithValue }) => {
    const res = await api.post<{ user: AuthUser }>('/auth/login-password', { email, password });
    if (!res.success) return rejectWithValue(res.error.message);
    return res.data.user;
  },
);

export const verifyMagicLink = createAsyncThunk('auth/verify', async (token: string, { rejectWithValue }) => {
  const res = await api.get<{ user: AuthUser }>(`/auth/verify?token=${token}`);
  if (!res.success) return rejectWithValue(res.error.message);
  return res.data.user;
});

export const logout = createAsyncThunk('auth/logout', async () => {
  await api.post('/auth/logout');
});

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    clearError(state) {
      state.error = null;
    },
    patchUser(state, action: PayloadAction<Partial<AuthUser>>) {
      if (state.user) {
        state.user = { ...state.user, ...action.payload };
      }
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch current user
      .addCase(fetchCurrentUser.pending, (state) => { state.loading = true; })
      .addCase(fetchCurrentUser.fulfilled, (state, action: PayloadAction<AuthUser>) => {
        state.user = action.payload;
        state.loading = false;
        state.initialized = true;
      })
      .addCase(fetchCurrentUser.rejected, (state) => {
        state.user = null;
        state.loading = false;
        state.initialized = true;
      })
      // Login with magic link
      .addCase(loginWithMagicLink.pending, (state) => { state.loading = true; state.error = null; })
      .addCase(loginWithMagicLink.fulfilled, (state) => { state.loading = false; })
      .addCase(loginWithMagicLink.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // Login with password
      .addCase(loginWithPassword.pending, (state) => { state.loading = true; state.error = null; })
      .addCase(loginWithPassword.fulfilled, (state, action: PayloadAction<AuthUser>) => {
        state.user = action.payload;
        state.loading = false;
        state.initialized = true;
      })
      .addCase(loginWithPassword.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // Verify magic link
      .addCase(verifyMagicLink.pending, (state) => { state.loading = true; state.error = null; })
      .addCase(verifyMagicLink.fulfilled, (state, action: PayloadAction<AuthUser>) => {
        state.user = action.payload;
        state.loading = false;
        state.initialized = true;
      })
      .addCase(verifyMagicLink.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // Logout
      .addCase(logout.fulfilled, (state) => {
        state.user = null;
        state.initialized = true;
      });
  },
});

export const { clearError, patchUser } = authSlice.actions;
export default authSlice.reducer;
